import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { getUser, getUUID, saveUser } from "../../../helpers/localStorage";
import { getAllApplications, getProfile, getAllDomains, confirmEmailPrompt, confirmWelcome, startTrial } from "../../../helpers/api";
import { addParameterToURL } from "../../../helpers/others";
import { getMantenimiento } from "../../../helpers/api";
import { getRemaining } from "../../../helpers/dueDate";
import { catalog, heavyDuty, dieselInstall, carsInstall } from "./catalog";
import moment from "moment";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import "./applications.css";

export default function ApplicationsPage() {
  const [userApplications, setUserApplications] = useState(
    getUser().applications
  );
  const [userDomains, setUserDomains] = useState(
    getUser().domains || []
  );
  // Fecha de vencimiento de la cuenta, para avisar al cliente del tiempo que le queda.
  const [dueDate, setDueDate] = useState(getUser().dueDate || null);
  // Fecha propia de cada app (para mostrarle al cliente cuanto le queda a cada una).
  const [userApplicationDates, setUserApplicationDates] = useState(getUser().applicationDates || []);
  // Aviso que el dueno enciende cuando el servicio esta caido. Va arriba del
  // todo: si el taller no puede trabajar, es lo primero que tiene que leer.
  const [mantenimiento, setMantenimientoEstado] = useState(null);
  // Aplicaciones que el dueno apago para el admin de este cliente. Se siguen
  // ensenando, pero como caidas: hacerlas desaparecer pareceria que nunca las
  // tuvo, y el cliente llamaria preguntando por que se le quito el servicio.
  const [appsCaidas, setAppsCaidas] = useState([]);
  // Apps prestadas porque la suya esta caida: [{ app, por }] (ver backend Monitor).
  const [prestadas, setPrestadas] = useState([]);
  // Pruebas gratis (1 hora, una vez por app) que el cliente ya uso o tiene en
  // curso. Las que estan en curso ya vienen sumadas en "applications".
  const [trials, setTrials] = useState([]);
  // Clientes de algunos admins no tienen prueba gratis (config.trial.sinPruebaClientesDe).
  const [sinPrueba, setSinPrueba] = useState(false);
  // App que el cliente toco para probar (abre la ventana de confirmacion).
  const [trialApp, setTrialApp] = useState(null);
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialError, setTrialError] = useState("");
  // DIESEL / CARS REMOTE SOFTWARE INSTALL: la lista abierta (null = cerrada)
  // y el programa elegido dentro de ella.
  const [remoto, setRemoto] = useState(null);
  const [dieselSel, setDieselSel] = useState(null);
  // Reloj para la cuenta regresiva de las pruebas en curso.
  const [ahora, setAhora] = useState(Date.now());

  const [config, setConfig] = useState({ applications: [] });
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Imagenes que no se pudieron cargar: se sustituyen por una placa de iniciales
  // en vez de dejar el icono de "imagen rota" del navegador.
  const [imgFallidas, setImgFallidas] = useState({});

  const [showModalServiceNotAvailable, setShowModalServiceNotAvailable] = useState(false);

  const [showModalAppDown, setShowModalAppDown] = useState(false);

  // Aviso de "registra tu correo real" (una sola vez por cliente). NO cambia el
  // usuario ni la forma de entrar: solo guarda un correo donde recibir el boton
  // de liberar navegadores. Se muestra hasta que el cliente responde (guarda o
  // salta); a partir de ahi no vuelve a aparecer (emailPromptDone).
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  // Ventana "Welcome to CardexApp" (una sola vez por cliente, welcomeDone). Ese
  // dia no se muestran los otros avisos, para no apilar ventanas; salen en la
  // siguiente entrada.
  const [showWelcome, setShowWelcome] = useState(false);
  const cerrarBienvenida = () => {
    setShowWelcome(false);
    confirmWelcome().catch(() => {});
  };
  // Recordatorio de vencimiento: cuando a la cuenta (o a una app) le quedan 3
  // dias o menos, se le muestra una ventana (una vez al dia).
  const [showExpiry, setShowExpiry] = useState(false);
  const [expiry, setExpiry] = useState(null);
  const [emailValue, setEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const isEmail = (t) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(t || "").trim());

  const cerrarAvisoCorreo = () => {
    // Marca el aviso como respondido aunque no haya escrito correo: no se insiste.
    setEmailSaving(true);
    confirmEmailPrompt("")
      .catch(() => {})
      .finally(() => {
        setEmailSaving(false);
        setShowEmailPrompt(false);
      });
  };

  const guardarCorreo = () => {
    if (!isEmail(emailValue)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setEmailSaving(true);
    confirmEmailPrompt(emailValue.trim())
      .then(() => setShowEmailPrompt(false))
      .catch((m) => setEmailError(m))
      .finally(() => setEmailSaving(false));
  };

  // Contenedor de la pantalla: GSAP limita sus selectores a este subarbol y
  // revierte todo solo al desmontar (useGSAP envuelve gsap.context).
  const rootRef = useRef(null);

  useEffect(() => {
    // Si falla, se ignora en silencio: un aviso que no carga no debe impedir
    // que el cliente vea sus aplicaciones.
    getMantenimiento()
      .then((m) => setMantenimientoEstado(m && m.activo ? m : null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getProfile()
      .then((data) => {
        setUserApplications(data.applications);
        setAppsCaidas([...(data.appsBloqueadas || []), ...(data.appsCaidas || [])]);
        setPrestadas(data.prestadas || []);
        setTrials(data.trials || []);
        setSinPrueba(!!data.sinPrueba);
        setUserDomains(data.domains || []);
        setDueDate(data.dueDate || null);
        setUserApplicationDates(data.applicationDates || []);

        // Actualizar localStorage con los datos más recientes
        const currentUser = getUser();
        saveUser({
          ...currentUser,
          applications: data.applications,
          domains: data.domains || [],
          dueDate: data.dueDate || null,
          applicationDates: data.applicationDates || []
        });

        // Aviso de correo (una sola vez). Se prellena con el correo que ya
        // tenga guardado o, si su usuario es un correo, con ese; asi al cliente
        // que ya esta bien solo le queda darle Save.
        if (!data.welcomeDone) {
          setShowWelcome(true);
        } else if (!data.emailPromptDone) {
          const sugerido = data.personalEmail
            ? data.personalEmail
            : (isEmail(currentUser.username) ? currentUser.username : "");
          setEmailValue(sugerido);
          setShowEmailPrompt(true);
        } else {
          // Recordatorio de vencimiento. Se junta la fecha de la cuenta con la
          // de cada app y se toma la mas proxima; si le quedan 3 dias o menos,
          // se muestra la ventana (una sola vez al dia, para no fastidiar).
          const fechas = [];
          if (data.dueDate) fechas.push(data.dueDate);
          (data.applicationDates || []).forEach((x) => {
            if (x && x.vence) fechas.push(x.vence);
          });
          // Se separa lo YA vencido (hasta 30 dias atras, para no revivir cosas
          // viejas) de lo por vencer (3 dias o menos). Lo vencido manda: si hay
          // algo caido, ese aviso gana. Si no, el que se vence mas pronto.
          let vencido = null;
          let porVencer = null;
          fechas.forEach((f) => {
            const r = getRemaining(f);
            if (r.days === null) return;
            if (r.days < 0 && r.days >= -30) {
              // el mas RECIENTE vencido (dias mas cercano a 0)
              if (vencido === null || r.days > vencido.days) {
                vencido = { days: r.days, date: f, expired: true };
              }
            } else if (r.days >= 0 && r.days <= 3) {
              if (porVencer === null || r.days < porVencer.days) {
                porVencer = { days: r.days, date: f, expired: false };
              }
            }
          });
          const masProximo = vencido || porVencer;
          if (masProximo) {
            const hoy = new Date().toISOString().slice(0, 10);
            const marca = `${currentUser.username}|${hoy}`;
            if (localStorage.getItem("igExpiryReminder") !== marca) {
              localStorage.setItem("igExpiryReminder", marca);
              setExpiry(masProximo);
              setShowExpiry(true);
            }
          }
        }
      })
      .catch(() => {});
  }, []);

  // Cada 30 s avanza el reloj; si una prueba acaba de vencer, se vuelve a pedir
  // el perfil para que la app pase sola a "Available to add".
  useEffect(() => {
    const t = setInterval(() => {
      const antes = ahora;
      const ya = Date.now();
      setAhora(ya);
      const vencioAlguna = trials.some((x) => {
        const v = new Date(x.vence).getTime();
        return v > antes && v <= ya;
      });
      if (vencioAlguna) {
        getProfile()
          .then((data) => {
            setUserApplications(data.applications);
            setAppsCaidas([...(data.appsBloqueadas || []), ...(data.appsCaidas || [])]);
            setPrestadas(data.prestadas || []);
            setTrials(data.trials || []);
            saveUser({ ...getUser(), applications: data.applications });
          })
          .catch(() => {});
      }
    }, 30000);
    return () => clearInterval(t);
  }, [ahora, trials]);

  useEffect(() => {
    getAllApplications()
      .then((data) => {
        setConfig(data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));

    getAllDomains()
      .then((data) => {
        if (data && data.domains && Array.isArray(data.domains)) {
          setDomains(data.domains.filter((d) => d.enabled));
        } else {
          setDomains([]);
        }
      })
      .catch(() => setDomains([]));
  }, []);

  // ---------------------------------------------------------------
  // Apertura de una aplicacion. Esta logica es la original, intacta:
  // los dominios se abren en otra pestana y las aplicaciones llevan
  // usuario, token, id y uuid en la direccion.
  // ---------------------------------------------------------------
  const openApp = (selectedApp) => {
    if (selectedApp.isDomain) {
      window.open(selectedApp.to, "_blank");
    } else {
      let url = addParameterToURL(selectedApp.to, "igN", getUser().username);
      url = addParameterToURL(url, "igT", getUser().accessToken);
      url = addParameterToURL(url, "igApp", selectedApp.id);
      url = addParameterToURL(url, "uuid", getUUID());
      window.location.href = url;
    }
  };

  // ---------------------------------------------------------------
  // Prueba gratis. Debe coincidir con backend/config.js -> trial.excluidas
  // (el servidor vuelve a comprobarlo todo; esto solo pinta el boton).
  // ---------------------------------------------------------------
  const TRIAL_EXCLUIDAS = [1, 4, 5, 7, 9, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
  const trialDe = (id) => trials.find((t) => Number(t.app) === Number(id));
  const trialEnCurso = (id) => {
    const t = trialDe(id);
    return t && new Date(t.vence).getTime() > ahora ? t : null;
  };
  const puedeProbar = (id) =>
    !sinPrueba && !TRIAL_EXCLUIDAS.includes(Number(id)) && !trialDe(id);

  const empezarPrueba = () => {
    if (!trialApp) return;
    setTrialError("");
    setTrialSaving(true);
    startTrial(trialApp.id)
      .then((t) => {
        const apps = [...new Set([...userApplications.map(Number), Number(trialApp.id)])];
        setTrials((prev) => [...prev, t]);
        setUserApplications(apps);
        saveUser({ ...getUser(), applications: apps });
        const app = trialApp;
        setTrialApp(null);
        openApp(app);
      })
      .catch((m) => setTrialError(String(m)))
      .finally(() => setTrialSaving(false));
  };

  const currentUser = getUser();
  const isAdmin = currentUser.type === 0 || currentUser.type === 1;

  // Las caidas cuentan como suyas: el backend ya las quito de "applications"
  // para que los gateways las rechacen, pero en pantalla tienen que seguir
  // apareciendo, marcadas.
  const esCaida = (id) => appsCaidas.map(Number).includes(Number(id));
  const prestadaDe = (id) => prestadas.find((p) => Number(p.app) === Number(id));

  // Si el dueno mueve o regresa clientes (App Status) mientras esta pantalla
  // esta abierta, se entera en un par de minutos sin tener que recargar.
  useEffect(() => {
    const t = setInterval(() => {
      getProfile()
        .then((data) => {
          setUserApplications(data.applications);
          setAppsCaidas([...(data.appsBloqueadas || []), ...(data.appsCaidas || [])]);
          setPrestadas(data.prestadas || []);
          saveUser({ ...getUser(), applications: data.applications });
        })
        .catch(() => {});
    }, 120000);
    return () => clearInterval(t);
  }, []);
  // Fecha propia de una app; si no tiene, cae en la de la cuenta (dueDate).
  const fechaDeApp = (id) => {
    const e = (userApplicationDates || []).find((x) => Number(x.app) === Number(id));
    return e && e.vence ? e.vence : dueDate;
  };
  // La app 12 ("Crear perfil de estimado") NO se pinta como una app normal: su
  // tarjeta la arma aparte "tarjetasPerfilTA" (con el enlace correcto a
  // ?destino=perfil). Sin esta exclusion saldria DOS veces.
  // Aplicaciones que NO se muestran en CardexApp. No se borran del sistema: la
  // aplicacion sigue en la base y su puerta de enlace sigue viva (otras
  // plataformas la usan), solo deja de ofrecerse aqui.
  //   13 = "PACCARNet" (el portal). El tablero de inicio de SharePoint no se
  //        puede servir bien por la puerta de enlace; las herramientas utiles
  //        van aparte: PACCAR RMI (14) y PACCAR ECAT (15).
  // NOTA: la app 1 ("All Data" viejo) se REACTIVO el 2026-09-17 (Humberto): su
  // puerta (Docker :8080 en la OptiPlex) sigue viva y dura mas que la V2 (11).
  // 13 PACCARNet; 1 AllData (clasica, reemplazada por V2), 5 Kanametek (prueba):
  // retiradas de la cuadricula. OJO: la 15 ("WorkshopData") es la que ahora sirve
  // HaynesPro (haynes.obd2sw.com) -> NO ocultar, se queda visible.
  const OCULTAS_EN_CARDEX = [13, 1, 5];
  // No se ofrecen en "Available to add", pero si salen a quien las tenga.
  // 14 WebDataBays: ningun cliente la tiene (Humberto, 2026-10-20).
  const NO_OFRECER = [14];

  const misApps = config.applications.filter(
    (a) =>
      (userApplications.includes(a.id) || esCaida(a.id)) &&
      a.id !== 12 &&
      !OCULTAS_EN_CARDEX.includes(a.id)
  );
  // Sin logo propio (salen solo las iniciales): al final de "Available to add".
  // 14 WebDataBays (W), 17 ALLDATA EU (AE), 18 TecDoc (T), 19 Ford PTS (FP).
  const AL_FINAL = [14, 17, 18, 19];
  const otrasApps = config.applications
    .filter(
      (a) =>
        !userApplications.includes(a.id) &&
        !esCaida(a.id) &&
        a.id !== 12 &&
        !OCULTAS_EN_CARDEX.includes(a.id) &&
        !NO_OFRECER.includes(a.id)
    )
    .sort((a, b) => AL_FINAL.includes(a.id) - AL_FINAL.includes(b.id));

  const userDomainStrings = (userDomains || []).map((id) => String(id));
  const misDominios = (Array.isArray(domains) ? domains : [])
    .filter((d) => (isAdmin ? true : userDomainStrings.includes(String(d._id))))
    .map((d) => ({
      img: d.img ? `${process.env.REACT_APP_API.replace("/api", "")}${d.img}` : "",
      tittle: d.name,
      description: d.description,
      to: d.url,
      isDomain: true,
    }));

  // Acceso al panel de cuentas de TechAdvisor para ciertos admins (p.ej.
  // walter): registran las cuentas de Connect de SUS empleados. Es una tarjeta
  // aparte, no una aplicacion que ellos usen. Aparece solo para los de la
  // lista; el gateway vuelve a comprobar el permiso, esto solo pinta el boton.
  const ADMINS_PANEL_TA = ["walter@gmail.com"];
  const tarjetasPanelTA = ADMINS_PANEL_TA.includes(
    String(currentUser.username || "").toLowerCase()
  )
    ? [
        {
          id: "ta-panel",
          tittle: "Cuentas de empleados",
          description:
            "Registrar o cambiar las cuentas de TechAdvisor de tus empleados",
          img: `${process.env.REACT_APP_DOMAIN}/img/connect-admin.svg`,
          to: "https://techadvisor.cardexapp.com/entrar?destino=panel",
        },
      ]
    : [];

  // Tarjeta "Mi perfil de estimado": lleva al empleado a crear/editar su
  // estimate profile en Mitchell Connect por el gateway (?destino=perfil).
  //
  // Aparece SOLO si el dueno le asigno el permiso temporal "Crear perfil de
  // estimado" (app 12). Es un interruptor de un solo uso: el dueno lo prende, el
  // cliente crea su perfil, y el dueno lo apaga y la tarjeta desaparece. No es la
  // app 7 (el uso normal de TechAdvisor).
  const tarjetasPerfilTA = userApplications.map(Number).includes(12)
    ? [
        {
          id: "ta-perfil",
          tittle: "Mi perfil de estimado",
          description:
            "Crea o edita tu perfil de estimados de TechAdvisor. Se guarda en Mitchell.",
          img: `${process.env.REACT_APP_DOMAIN}/img/perfil-estimado.svg`,
          to: "https://techadvisor.cardexapp.com/entrar?destino=perfil",
        },
      ]
    : [];

  const disponibles = [
    ...misApps,
    ...misDominios,
    ...tarjetasPanelTA,
    ...tarjetasPerfilTA,
  ];

  // ---------------------------------------------------------------
  // El saludo "Welcome back" entra suave al montar la pantalla.
  // ---------------------------------------------------------------
  useGSAP(
    () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      gsap.from(".ig-apps-hero-main > *", {
        y: 22,
        autoAlpha: 0,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.1,
        clearProps: "transform,opacity,visibility",
      });
      gsap.from(".ig-apps-status", {
        autoAlpha: 0,
        y: 14,
        duration: 0.5,
        delay: 0.25,
        ease: "power2.out",
        clearProps: "transform,opacity,visibility",
      });
    },
    { scope: rootRef }
  );

  // ---------------------------------------------------------------
  // Entrada coreografiada con GSAP: cuando ya hay tarjetas, entran en
  // cascada con un pequeno rebote (back.out) que las hace sentir vivas.
  // Se respeta prefers-reduced-motion, y con clearProps se limpia el
  // transform al terminar para que el hover siga funcionando.
  // Si el JS fallara, el CSS de respaldo (ig-card-in) sigue mostrando todo.
  // ---------------------------------------------------------------
  useGSAP(
    () => {
      if (isLoading) return; // esperar a que esten las tarjetas reales
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      gsap.from(".ig-app-card", {
        y: 26,
        autoAlpha: 0,
        scale: 0.94,
        duration: 0.55,
        ease: "back.out(1.5)",
        stagger: { amount: 0.7, from: "start" },
        clearProps: "transform,opacity,visibility",
      });
    },
    { scope: rootRef, dependencies: [isLoading, disponibles.length, otrasApps.length] }
  );

  // ---------------------------------------------------------------
  // Hover mas marcado con GSAP: la tarjeta se eleva y su logo hace zoom,
  // con una transicion suave e interrumpible (overwrite:"auto"). Se usa
  // delegacion sobre el contenedor para que funcione con tarjetas que
  // aparecen despues, con una sola pareja de listeners y limpieza al salir.
  // El transform del hover CSS se apaga bajo .js-gsap para que no choquen;
  // la sombra del CSS se mantiene, y el foco por teclado conserva el CSS.
  // ---------------------------------------------------------------
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const animar = (card, encima) => {
      const logo = card.querySelector(".ig-app-logo");
      gsap.to(card, {
        y: encima ? -12 : 0,
        scale: encima ? 1.035 : 1,
        duration: encima ? 0.32 : 0.45,
        ease: "power3.out",
        overwrite: "auto",
      });
      if (logo) {
        gsap.to(logo, {
          scale: encima ? 1.12 : 1,
          duration: encima ? 0.38 : 0.45,
          ease: "power3.out",
          overwrite: "auto",
        });
      }
    };
    const onOver = (e) => {
      const card = e.target.closest(".ig-app-card");
      if (card && root.contains(card)) animar(card, true);
    };
    const onOut = (e) => {
      const card = e.target.closest(".ig-app-card");
      if (card && !card.contains(e.relatedTarget)) animar(card, false);
    };
    root.addEventListener("mouseover", onOver);
    root.addEventListener("mouseout", onOut);
    return () => {
      root.removeEventListener("mouseover", onOver);
      root.removeEventListener("mouseout", onOut);
    };
  }, []);

  const renderCard = (app, i, { locked = false } = {}) => {
    const caida = !locked && esCaida(app.id);
    const probable = locked && puedeProbar(app.id);
    const enPrueba = !locked && trialEnCurso(app.id);
    const prestada = !locked && !caida && prestadaDe(app.id);
    const nombrePor = prestada
      ? ((config.applications.find((a) => Number(a.id) === Number(prestada.por)) || {}).tittle || "Your app")
      : "";
    return (
      <button
        type="button"
        key={`${locked ? "l" : "a"}-${i}`}
        className={`ig-app-card${locked ? " ig-app-card-locked" : ""}${probable ? " ig-app-card-trial" : ""}${caida ? " ig-app-card-down" : ""}`}
        style={{ animationDelay: `${Math.min(i, 8) * 70}ms` }}
        onClick={() =>
          caida
            ? setShowModalAppDown(true)
            : probable
            ? (setTrialError(""), setTrialApp(app))
            : locked
            ? setShowModalServiceNotAvailable(true)
            : openApp(app)
        }
        /* El nombre no se ve, pero los lectores de pantalla lo necesitan */
        aria-label={
          caida
            ? `${app.tittle} — temporarily down`
            : probable
            ? `${app.tittle} — try it free for 1 hour`
            : locked
            ? `${app.tittle} — not activated`
            : `Open ${app.tittle}`
        }
        title={
          caida
            ? `${app.tittle} — temporarily down`
            : probable
            ? `${app.tittle} — try it free for 1 hour`
            : locked
            ? `${app.tittle} — not activated`
            : app.tittle
        }
      >
        {caida && <span className="ig-app-down-tag">App down</span>}
        {probable && <span className="ig-app-trial-tag">Try 1 hour free</span>}
        {prestada && (
          <span className="ig-app-remaining ig-app-remaining--warning">
            <span className="ig-app-remaining-dot" />
            Temporary · {nombrePor} is down
          </span>
        )}
        {enPrueba && (() => {
          const min = Math.max(1, Math.ceil((new Date(enPrueba.vence).getTime() - ahora) / 60000));
          return (
            <span className="ig-app-remaining ig-app-remaining--warning">
              <span className="ig-app-remaining-dot" />
              Trial · {min} min left
            </span>
          );
        })()}
        {!locked && !enPrueba && !prestada && (() => {
          const f = fechaDeApp(app.id);
          if (!f) return null;
          const r = getRemaining(f);
          if (r.level === "none") return null;
          return (
            <span className={`ig-app-remaining ig-app-remaining--${r.level}`}>
              <span className="ig-app-remaining-dot" />
              {r.text}
            </span>
          );
        })()}
        {app.img && !imgFallidas[app.img] ? (
          <img
            className="ig-app-logo"
            src={app.img}
            alt=""
            onError={() => setImgFallidas((prev) => ({ ...prev, [app.img]: true }))}
          />
        ) : (
          /* Sin imagen: iniciales, para que la tarjeta no quede vacia */
          <span className="ig-app-initial">
            {String(app.tittle || "?")
              .split(/\s+/)
              .slice(0, 2)
              .map((w) => w.charAt(0))
              .join("")
              .toUpperCase()}
          </span>
        )}

        {/* Candado en las no activadas: sin texto, es la unica pista visual */}
        {locked && (
          <span className="ig-app-lock" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M17 10V8a5 5 0 0 0-10 0v2H5v12h14V10h-2zm-8-2a3 3 0 0 1 6 0v2H9V8z" />
            </svg>
          </span>
        )}
      </button>
    );
  };

  const CATALOGOS_REMOTOS = [
    {
      key: "diesel",
      titulo: "Diesel Remote Software Install",
      intro: "Diesel software we can install remotely on your computer.",
      // Foto: Freightliner eCascadia, Wikimedia Commons, licencia CC0.
      img: "/img/diesel-remote-install-photo.jpg",
      items: dieselInstall,
    },
    {
      key: "cars",
      titulo: "Cars Remote Software Install",
      intro: "Car software we can install remotely on your computer.",
      // Foto: Ford Mustang GT 2024, Wikimedia Commons, licencia CC0.
      img: "/img/cars-remote-install-photo.jpg",
      items: carsInstall,
    },
  ];

  const renderSkeletons = () =>
    [0, 1, 2].map((i) => (
      <div className="ig-app-card ig-app-skeleton" key={`s-${i}`} style={{ animationDelay: `${i * 90}ms` }} />
    ));

  const remaining = getRemaining(dueDate);

  return (
    <>
      {mantenimiento && (
        <div className="ig-mantenimiento" role="status">
          <span className="ig-mantenimiento-punto" aria-hidden="true" />
          <div className="ig-mantenimiento-texto">
            <strong>{mantenimiento.titulo}</strong>
            <span>{mantenimiento.mensaje}</span>
          </div>
        </div>
      )}

      <Modal
        show={showModalAppDown}
        onHide={() => setShowModalAppDown(false)}
        centered={true}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>App down</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This application is temporarily unavailable. Please contact your
          provider — it will come back on as soon as the service is restored.
        </Modal.Body>
      </Modal>

      <Modal show={showExpiry} onHide={() => setShowExpiry(false)} centered={true}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            {expiry && expiry.expired
              ? "Your subscription has expired"
              : "Your subscription is about to expire"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: "8px", fontWeight: 600 }}>
            {expiry && expiry.expired
              ? "Your subscription has expired."
              : expiry && expiry.days === 0
              ? "Your subscription expires today."
              : `Your subscription expires in ${expiry ? expiry.days : ""} day${
                  expiry && expiry.days === 1 ? "" : "s"
                }.`}
          </p>
          <p style={{ color: "#6b7a90", marginBottom: 0 }}>
            {expiry && expiry.expired ? (
              <>
                It ended on{" "}
                <strong>
                  {expiry ? moment(expiry.date).format("MMMM D, YYYY") : ""}
                </strong>
                . Renew now to restore your access.
              </>
            ) : (
              <>
                It ends on{" "}
                <strong>
                  {expiry ? moment(expiry.date).format("MMMM D, YYYY") : ""}
                </strong>
                . Please renew to keep your access without interruption.
              </>
            )}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowExpiry(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModalServiceNotAvailable}
        onHide={() => setShowModalServiceNotAvailable(false)}
        animation={false}
        centered={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Service Not Available</Modal.Title>
        </Modal.Header>
        <Modal.Body>Contact to admin, to activate this service</Modal.Body>
      </Modal>

      <Modal
        show={!!remoto}
        onHide={() => setRemoto(null)}
        centered={true}
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            {dieselSel ? dieselSel.tittle : remoto ? remoto.titulo : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dieselSel ? (
            <div className="ig-diesel-detail">
              <img src={dieselSel.img} alt={dieselSel.tittle} />
              <div className="ig-diesel-version">{dieselSel.version}</div>
              <p>{dieselSel.description}</p>
              <p className="ig-diesel-note">
                We install it remotely on your computer. Contact your
                administrator to schedule the installation.
              </p>
              <Button variant="link" style={{ paddingLeft: 0 }} onClick={() => setDieselSel(null)}>
                ← All programs
              </Button>
            </div>
          ) : (
            <>
              <p style={{ color: "#6b7a90", marginTop: 0 }}>
                {remoto ? remoto.intro : ""} Tap a program to see what it does.
              </p>
              <div className="ig-diesel-grid">
                {(remoto ? remoto.items : []).map((d) => (
                  <button
                    type="button"
                    key={d.key}
                    className="ig-diesel-item"
                    onClick={() => setDieselSel(d)}
                  >
                    <span className="ig-diesel-img">
                      <img src={d.img} alt="" loading="lazy" />
                    </span>
                    <span className="ig-diesel-name">{d.tittle}</span>
                    <span className="ig-diesel-ver">{d.version}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!trialApp}
        onHide={() => !trialSaving && setTrialApp(null)}
        animation={false}
        centered={true}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.15rem" }}>
            Free trial{trialApp ? ` — ${trialApp.tittle}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: ".5rem", color: "#444" }}>
            Try this application free for <strong>1 hour</strong>, starting now.
          </p>
          <p style={{ marginBottom: 0, color: "#6b7a90" }}>
            You can use the free trial only <strong>once</strong> for this
            application. When the hour is over it will be locked again — contact
            your provider to add it to your account.
          </p>
          {trialError ? (
            <div style={{ color: "#c0392b", fontSize: ".9rem", marginTop: ".75rem" }}>
              {trialError}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            style={{ color: "#888" }}
            onClick={() => setTrialApp(null)}
            disabled={trialSaving}
          >
            Not now
          </Button>
          <Button variant="primary" onClick={empezarPrueba} disabled={trialSaving}>
            {trialSaving ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : null}{" "}
            Start free trial
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showWelcome}
        onHide={cerrarBienvenida}
        animation={false}
        centered={true}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.25rem" }}>
            Welcome to CardexApp
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: 0, color: "#444" }}>
            All your repair information in one place. Tap any application below
            to open it.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="warning" onClick={cerrarBienvenida}>
            Get started
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEmailPrompt}
        onHide={cerrarAvisoCorreo}
        animation={false}
        centered={true}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            Confirm your email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: "1rem", color: "#444" }}>
            Please confirm the email where you want to receive account messages,
            such as the button to release your browsers if you ever reach the
            device limit. This does not change how you sign in.
          </p>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              guardarCorreo();
            }}
          >
            <Form.Control
              type="email"
              placeholder="your@email.com"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              autoFocus
            />
            {emailError ? (
              <div style={{ color: "#c0392b", fontSize: ".9rem", marginTop: ".5rem" }}>
                {emailError}
              </div>
            ) : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            style={{ color: "#888" }}
            onClick={cerrarAvisoCorreo}
            disabled={emailSaving}
          >
            Not now
          </Button>
          <Button variant="warning" onClick={guardarCorreo} disabled={emailSaving}>
            {emailSaving ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : null}{" "}
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="ig-apps js-gsap" ref={rootRef}>
        <header className="ig-apps-hero">
          <div className="ig-apps-hero-main">
            <p className="ig-apps-eyebrow">Welcome back</p>
            <h1 className="ig-apps-name">{currentUser.username}</h1>
            <p className="ig-apps-sub">
              {disponibles.length > 0
                ? `You have ${disponibles.length} ${disponibles.length === 1 ? "service" : "services"} available.`
                : "Your services will appear here."}
            </p>
          </div>

          {remaining.level !== "none" && (
            <div className={`ig-apps-status ig-apps-status-${remaining.level}`}>
              <span className="ig-apps-status-dot" aria-hidden="true" />
              <span className="ig-apps-status-text">
                <strong>{remaining.text}</strong>
                <small>
                  {remaining.level === "expired" ? "Expired " : "Renews "}
                  {moment(dueDate).format("MMM D, YYYY")}
                  {remaining.level !== "ok" && " · contact your provider to renew"}
                </small>
              </span>
            </div>
          )}
        </header>

        <section className="ig-apps-section">
          <div className="ig-apps-section-head">
            <h2>Your applications</h2>
            <span className="ig-apps-rule" aria-hidden="true" />
          </div>

          <div className="ig-apps-grid">
            {isLoading
              ? renderSkeletons()
              : disponibles.length > 0
              ? disponibles.map((app, i) => renderCard(app, i))
              : (
                <p className="ig-apps-empty">
                  No services are active on your account yet. Contact your provider to get started.
                </p>
              )}
            {/* Para TODOS los clientes: catalogos de programas que se instalan
                a distancia (ver catalog.js -> dieselInstall / carsInstall).
                Una tarjeta sin programas no se muestra. */}
            {!isLoading &&
              CATALOGOS_REMOTOS.filter((c) => c.items.length > 0).map((c, k) => (
                <button
                  type="button"
                  key={c.key}
                  className="ig-app-card ig-app-card-diesel"
                  style={{ animationDelay: `${Math.min(disponibles.length + k, 8) * 70}ms` }}
                  onClick={() => { setDieselSel(null); setRemoto(c); }}
                  aria-label={c.titulo}
                  title={c.titulo}
                >
                  <img className="ig-app-logo" src={c.img} alt="" />
                </button>
              ))}
          </div>
        </section>


        {!isLoading && otrasApps.length > 0 && (
          <section className="ig-apps-section ig-apps-section-muted">
            <div className="ig-apps-section-head">
              <h2>Available to add</h2>
              <span className="ig-apps-rule" aria-hidden="true" />
            </div>
            <div className="ig-apps-grid">
              {/* Solo las aplicaciones REALES de la plataforma que este cliente
                  aun no tiene: son las que puede pedir. */}
              {otrasApps.map((app, i) => renderCard(app, i, { locked: true }))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
